import { useCallback } from 'react';
import type { GoodsFirstPageRenderingModel } from '../../../../api/types';
import { bridge } from '../../../bridge';
import { getWebBaseURL } from '../../../getWebBaseURL';
import {
  appendParams,
  buildUrl,
  finalizeUrl,
  getDefaultDeeplink,
} from '../../utils';
import { useExperimentGroup } from '../../../../context/UserContext';
import { isWeb } from '../../../isWeb';
import { useRootStackNavigation } from '../../../../hooks/useRootNavigation';
import { useInstanceId } from '../../../../hooks';

type MoveToGoodsProps = {
  sno: number;
  firstPageRendering?: GoodsFirstPageRenderingModel | null;
  loggingAnalytics?: Record<string, unknown>;
  couponSno?: number;
  alwaysRN?: boolean;
};

const DEBUG_MODE = __DEV__;

export const useMoveToGoods = () => {
  const isGoodsDetailRN = useExperimentGroup({
    sno: isWeb ? 'EXP1615' : `EXP1577`,
    targetGroups: ['B'],
  });

  const navigation = useRootStackNavigation();
  const instanceId = useInstanceId();

  return useCallback(
    ({
      firstPageRendering,
      sno,
      loggingAnalytics,
      couponSno,
      alwaysRN,
    }: MoveToGoodsProps) => {
      const experimentGroup = isGoodsDetailRN || alwaysRN || DEBUG_MODE;
      const getDeeplink = () => {
        if (experimentGroup) {
          const url = getDefaultDeeplink();
          appendParams(url, 'screen', 'GoodsDetail');
          return url;
        }

        return buildUrl(`ably://goods/${sno}`);
      };
      const extras = {
        sno,
        ...(loggingAnalytics ? loggingAnalytics : {}),
        ...(firstPageRendering ? firstPageRendering : {}),
        ...(couponSno ? { coupon_sno: couponSno } : {}),
      };
      const goodsDetailDeeplink = getDeeplink();
      appendParams(goodsDetailDeeplink, 'extras', JSON.stringify(extras));

      return bridge.openDeeplink(finalizeUrl(goodsDetailDeeplink), {
        webCallback: async () => {
          if (experimentGroup) {
            navigation.push('GoodsDetail', {
              instanceId,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              goodsId: sno,
              extras: JSON.stringify(extras),
            });
          } else {
            const baseURL = await getWebBaseURL();
            location.href = `${baseURL}/goods/${sno}`;
          }
        },
      });
    },
    [isGoodsDetailRN, navigation, instanceId],
  );
};
